import { Button } from 'designSystem/components/Button'
import { Text } from 'designSystem/components/Text'
import {
  ToggleGroup,
  ToggleGroupItem,
} from 'designSystem/components/ToggleGroup'
import { ComponentHealthCondition } from 'features/fde/assetHealthPlanning/componentHealthConditions'
import {
  generateSiteViewCheckerboardColor,
  useSiteViewColors,
} from 'features/overview/utils/siteViewTheme'
import React, { FC, useId } from 'react'
import styled from 'styled-components'
import { useGeckoTheme } from 'styles/GeckoTheme'
import { NoRLHealthScore } from 'types'

export type HealthConditionChipOption =
  | ComponentHealthCondition
  | NoRLHealthScore.NotEnoughData
  | NoRLHealthScore.NoInspection

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 1.25rem;
`

const ChipGroup = styled(ToggleGroup)`
  width: 100%;
  flex-wrap: wrap;
`

const Chip = styled(ToggleGroupItem)<{ $accent: string }>`
  &[data-state='on'] {
    border-color: ${({ $accent }) => $accent};
    box-shadow: 0 0 0 0.0625rem ${({ $accent }) => $accent};
  }
`

const Swatch = styled.span<{ $background: string; $borderColor?: string }>`
  display: inline-block;
  width: 0.5rem;
  height: 0.5rem;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${({ $background }) => $background};
  border: ${({ $borderColor }) =>
    $borderColor ? `0.0625rem solid ${$borderColor}` : 'none'};
`

const getDisplayLabel = (option: HealthConditionChipOption): string => {
  switch (option) {
    case NoRLHealthScore.NotEnoughData:
      return 'Not enough data'
    case NoRLHealthScore.NoInspection:
      return 'No inspection'
    default:
      return option
  }
}

interface HealthConditionChipGroupProps {
  label: string
  options: readonly HealthConditionChipOption[]
  value: readonly HealthConditionChipOption[]
  onChange: (value: HealthConditionChipOption[]) => void
}

/**
 * Multi-select of risk categories as toggle chips, colored like the asset
 * tracker legend. An empty selection means "show everything", which the header
 * says out loud instead of leaving the row blank.
 */
export const HealthConditionChipGroup: FC<HealthConditionChipGroupProps> = ({
  label,
  options,
  value,
  onChange,
}) => {
  const colors = useSiteViewColors()
  const { theme } = useGeckoTheme()
  const labelId = useId()

  const isOption = (
    candidate: string
  ): candidate is HealthConditionChipOption =>
    options.some((option) => option === candidate)

  const swatchFor = (option: HealthConditionChipOption) =>
    option === NoRLHealthScore.NotEnoughData ? (
      <Swatch
        aria-hidden
        $background={generateSiteViewCheckerboardColor(
          colors[option],
          theme.forecast.background1
        )}
        $borderColor={colors[option]}
      />
    ) : (
      <Swatch aria-hidden $background={colors[option]} />
    )

  return (
    <Container>
      <HeaderRow>
        <Text
          as='span'
          id={labelId}
          variant='caption'
          weight='medium'
          color='muted'>
          {label}
        </Text>
        {value.length > 0 ? (
          <Button
            variant='ghostMuted'
            size='xs'
            type='button'
            onClick={() => onChange([])}>
            Clear
          </Button>
        ) : (
          <Text as='span' variant='caption' color='tertiary'>
            Showing all
          </Text>
        )}
      </HeaderRow>
      <ChipGroup
        type='multiple'
        variant='outline'
        size='sm'
        spacing={2}
        aria-labelledby={labelId}
        value={[...value]}
        onValueChange={(next: string[]) => onChange(next.filter(isOption))}>
        {options.map((option) => (
          <Chip key={option} value={option} $accent={colors[option]}>
            {swatchFor(option)}
            {getDisplayLabel(option)}
          </Chip>
        ))}
      </ChipGroup>
    </Container>
  )
}
